import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faFilePdf,
  faSpinner,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import BoardResultsPosterCard from './BoardResultsPosterCard';
import {
  capturePosterElement,
  capturePosterObjectUrl,
  downloadBlob,
  exportFlyerPdf,
  fetchPosterPhoto,
  posterFilename,
  revokePhotoUrl,
  uploadPosterPhoto
} from '../utils/exportBoardResultsPosters';
import './BoardResultsPosterPreview.css';

export default function BoardResultsPosterPreview({
  row,
  photoUrl: photoUrlProp,
  apiBaseUrl,
  authHeaders,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onPhotoReplaced,
  customCard = null,
  hideReplace = false,
  title = '',
  subtitle = '',
  downloadName = '',
  pdfDownloadName = '',
  layout = 'portrait',
  /** When set (flyer), preview/download use the same html2canvas capture as PNG export. */
  getCaptureElement = null,
  captureKey = ''
}) {
  const posterRef = useRef(null);
  const fileRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(photoUrlProp || null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [captureUrl, setCaptureUrl] = useState(null);
  const [captureBlob, setCaptureBlob] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState('');
  const useCapturePreview = layout === 'flyer' && typeof getCaptureElement === 'function';
  const canDownloadPdf = layout === 'flyer' && typeof getCaptureElement === 'function';

  useEffect(() => {
    setPhotoUrl(photoUrlProp || null);
    if (photoUrlProp || !row?.id || !apiBaseUrl) return undefined;
    let cancelled = false;
    let created = null;
    fetchPosterPhoto(apiBaseUrl, authHeaders, row.id)
      .then((url) => {
        if (cancelled) {
          revokePhotoUrl(url);
          return;
        }
        created = url;
        if (url) setPhotoUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (created && created !== photoUrlProp) revokePhotoUrl(created);
    };
  }, [row?.id, photoUrlProp, apiBaseUrl, authHeaders]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') onPrev?.();
      if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  // Flyer: show the exact same raster as Download PNG
  useEffect(() => {
    if (!useCapturePreview) return undefined;
    let cancelled = false;
    let objectUrl = null;

    async function run() {
      setCapturing(true);
      setCaptureError('');
      setCaptureUrl(null);
      setCaptureBlob(null);
      try {
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        const el = getCaptureElement();
        if (!el) throw new Error('Flyer not ready — close and open preview again');
        const { blob, url } = await capturePosterObjectUrl(el);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setCaptureBlob(blob);
        setCaptureUrl(url);
      } catch (err) {
        if (!cancelled) setCaptureError(err.message || 'Could not render flyer preview');
      } finally {
        if (!cancelled) setCapturing(false);
      }
    }

    run();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [useCapturePreview, getCaptureElement, captureKey]);

  if (!row && !customCard && !useCapturePreview) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (useCapturePreview && captureBlob) {
        downloadBlob(captureBlob, downloadName || 'flyer.png');
        return;
      }
      const el = posterRef.current || (getCaptureElement && getCaptureElement());
      if (!el) throw new Error('Poster element missing');
      const blob = await capturePosterElement(el);
      downloadBlob(blob, downloadName || posterFilename(row));
    } catch (err) {
      alert(`Could not save poster: ${err.message || err}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!canDownloadPdf) return;
    setDownloadingPdf(true);
    try {
      const el = getCaptureElement();
      if (!el) throw new Error('Flyer not ready — close and open preview again');
      await exportFlyerPdf(el, pdfDownloadName || downloadName?.replace(/\.png$/i, '.pdf') || 'flyer_21x8ft.pdf');
    } catch (err) {
      alert(`Could not save flyer PDF: ${err.message || err}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleReplacePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !row?.id) return;
    setReplacing(true);
    try {
      const url = await uploadPosterPhoto(apiBaseUrl, authHeaders, row.id, file);
      if (!url) throw new Error('Photo saved but could not reload');
      setPhotoUrl(url);
      onPhotoReplaced?.(row.id, url);
    } catch (err) {
      alert(err.message || 'Could not replace photo');
    } finally {
      setReplacing(false);
    }
  };

  return (
    <div className="br-poster-lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label="Poster preview">
      <button
        type="button"
        className="br-poster-lightbox-close"
        onClick={onClose}
        title="Close (Esc)"
        aria-label="Close preview"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      {hasPrev && (
        <button
          type="button"
          className="br-poster-lightbox-nav is-prev"
          onClick={(e) => {
            e.stopPropagation();
            onPrev?.();
          }}
          title="Previous (←)"
          aria-label="Previous poster"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      <div
        className={`br-poster-lightbox-stage${layout === 'flyer' ? ' is-flyer' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {useCapturePreview ? (
          <div className="br-poster-lightbox-capture">
            {capturing && (
              <div className="br-poster-lightbox-capture-status">
                <FontAwesomeIcon icon={faSpinner} spin /> Rendering flyer preview…
              </div>
            )}
            {captureError && (
              <div className="br-poster-lightbox-capture-status is-error">{captureError}</div>
            )}
            {captureUrl && (
              <img
                src={captureUrl}
                alt={title || 'Flyer preview'}
                className="br-poster-lightbox-capture-img"
              />
            )}
          </div>
        ) : (
          <div className="br-poster-lightbox-card">
            <div className="br-poster-lightbox-scale">
              {customCard
                ? React.cloneElement(customCard, { ref: posterRef })
                : <BoardResultsPosterCard ref={posterRef} row={row} photoUrl={photoUrl} />}
            </div>
          </div>
        )}
        <div className="br-poster-lightbox-bar">
          <div className="br-poster-lightbox-meta">
            <strong>
              {title || (row ? `#${row.rank_no} ${row.student_name}` : 'Poster')}
            </strong>
            <span>
              {subtitle || (row ? `Roll ${row.roll_no}${row.percentage_label ? ` · ${row.percentage_label}` : ''}` : '')}
            </span>
          </div>
          <div className="br-poster-lightbox-actions">
            {!hideReplace && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/jpg,.jpg,.jpeg,.png,.webp"
                  hidden
                  onChange={handleReplacePhoto}
                />
                <button
                  type="button"
                  className="board-results-btn secondary board-results-btn-sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={replacing || !row?.id}
                  title="Replace student photo"
                >
                  <FontAwesomeIcon icon={replacing ? faSpinner : faCamera} spin={replacing} />
                  {replacing ? ' Replacing…' : ' Replace photo'}
                </button>
              </>
            )}
            <button
              type="button"
              className="board-results-btn secondary board-results-btn-sm"
              onClick={handleDownload}
              disabled={downloading || downloadingPdf || (useCapturePreview && (capturing || !captureBlob))}
            >
              <FontAwesomeIcon icon={downloading ? faSpinner : faDownload} spin={downloading} />
              {downloading ? ' Saving…' : ' Download PNG'}
            </button>
            {canDownloadPdf && (
              <button
                type="button"
                className="board-results-btn primary board-results-btn-sm"
                onClick={handleDownloadPdf}
                disabled={downloading || downloadingPdf || capturing}
                title="High-quality PDF · 21 ft × 8 ft page"
              >
                <FontAwesomeIcon icon={downloadingPdf ? faSpinner : faFilePdf} spin={downloadingPdf} />
                {downloadingPdf ? ' Saving PDF…' : ' Download PDF'}
              </button>
            )}
          </div>
        </div>
      </div>

      {hasNext && (
        <button
          type="button"
          className="br-poster-lightbox-nav is-next"
          onClick={(e) => {
            e.stopPropagation();
            onNext?.();
          }}
          title="Next (→)"
          aria-label="Next poster"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  );
}
